import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Loader2, StickyNote } from 'lucide-react';
import {
  Button,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Textarea,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@roger/ui-components';
import {
  createBillingCycleNote,
  listBillingCycleNotes,
} from '../../services/api';
import type {
  BillingCycleNoteCreate,
  BillingCycleNoteResponse,
  BillingCycleTabCounts,
  EpisodicClaimStatus,
} from '../../services/api/types.gen';
import { useStore } from '../../store';

export interface BillingCycleNoteButtonProps {
  billingCycleId: string;
  billingCycleStatus: EpisodicClaimStatus;
  isDraftReady?: boolean | null;
  patientName?: string;
  noteCountsByTab?: BillingCycleTabCounts;
}

function buildCreatePayload(
  body: string,
  billingCycleStatus: EpisodicClaimStatus,
  isDraftReady?: boolean | null
): BillingCycleNoteCreate {
  if (billingCycleStatus === 'DRAFT') {
    return {
      body,
      billing_cycle_status: billingCycleStatus,
      is_draft_ready: isDraftReady ?? false,
    };
  }
  return {
    body,
    billing_cycle_status: billingCycleStatus,
  };
}

function formatAuthorName(note: BillingCycleNoteResponse): string {
  const author = note.created_by;
  if (!author) {
    return 'Unknown user';
  }
  const fullName = [author.first_name, author.last_name]
    .filter(Boolean)
    .join(' ')
    .trim();
  return fullName || author.username;
}

function formatNoteTimestamp(createdAt: string): string {
  const date = new Date(createdAt);
  if (Number.isNaN(date.getTime())) {
    return '';
  }
  return date.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
}

function sortNotesOldestFirst(
  notes: BillingCycleNoteResponse[]
): BillingCycleNoteResponse[] {
  return [...notes].sort(
    (a, b) =>
      new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
  );
}

function totalNoteCount(
  notes: BillingCycleNoteResponse[],
  noteCountsByTab?: BillingCycleTabCounts
): number {
  if (notes.length > 0) {
    return notes.length;
  }
  if (!noteCountsByTab) {
    return 0;
  }
  return Object.values(noteCountsByTab).reduce((sum, count) => {
    return sum + (typeof count === 'number' ? count : 0);
  }, 0);
}

const BillingCycleNoteButton: React.FC<BillingCycleNoteButtonProps> = ({
  billingCycleId,
  billingCycleStatus,
  isDraftReady,
  patientName,
  noteCountsByTab: initialNoteCountsByTab,
}) => {
  const showError = useStore(state => state.showError);
  const showSuccess = useStore(state => state.showSuccess);
  const [open, setOpen] = useState(false);
  const [draftBody, setDraftBody] = useState('');
  const [existingNotes, setExistingNotes] = useState<
    BillingCycleNoteResponse[]
  >([]);
  const [noteCountsByTab, setNoteCountsByTab] = useState<
    BillingCycleTabCounts | undefined
  >(initialNoteCountsByTab);
  const [isLoadingNotes, setIsLoadingNotes] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const messagesListRef = useRef<HTMLUListElement>(null);

  const loadNotes = useCallback(async () => {
    setIsLoadingNotes(true);
    try {
      const response = await listBillingCycleNotes({
        path: { billing_cycle_id: billingCycleId },
      });
      if (response.error) {
        throw response.error;
      }
      const notes = response.data ?? [];
      setExistingNotes(notes);
    } catch (error) {
      console.error('Failed to load billing cycle notes:', error);
      showError('Unable to load notes', 'Please try again.');
      setExistingNotes([]);
    } finally {
      setIsLoadingNotes(false);
    }
  }, [billingCycleId, showError]);

  useEffect(() => {
    setNoteCountsByTab(initialNoteCountsByTab);
  }, [initialNoteCountsByTab]);

  useEffect(() => {
    if (!open) {
      return;
    }
    void loadNotes();
  }, [open, loadNotes]);

  const handleOpenChange = (nextOpen: boolean) => {
    if (isSaving) {
      return;
    }
    setOpen(nextOpen);
    if (!nextOpen) {
      setDraftBody('');
    }
  };

  const handleSave = async () => {
    const trimmed = draftBody.trim();
    if (!trimmed) {
      return;
    }

    setIsSaving(true);
    try {
      const response = await createBillingCycleNote({
        path: { billing_cycle_id: billingCycleId },
        body: buildCreatePayload(trimmed, billingCycleStatus, isDraftReady),
      });
      if (response.error) {
        throw response.error;
      }
      showSuccess('Note saved', 'Your note was added to this billing cycle.');
      setDraftBody('');
      await loadNotes();
    } catch (error) {
      console.error('Failed to save billing cycle note:', error);
      showError('Failed to save note', 'Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const noteCount = totalNoteCount(existingNotes, noteCountsByTab);
  const sortedNotes = useMemo(
    () => sortNotesOldestFirst(existingNotes),
    [existingNotes]
  );

  useLayoutEffect(() => {
    if (!open || isLoadingNotes) {
      return;
    }
    const list = messagesListRef.current;
    if (list) {
      list.scrollTop = list.scrollHeight;
    }
  }, [open, isLoadingNotes, sortedNotes]);
  const ariaLabel = patientName
    ? `Notes for ${patientName}`
    : 'Billing cycle notes';
  const tooltipLabel =
    noteCount > 0
      ? `Notes (${noteCount > 9 ? '9+' : noteCount})`
      : 'Add a note';

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <TooltipProvider delayDuration={300}>
        <Tooltip>
          <TooltipTrigger asChild>
            <PopoverTrigger asChild>
              <Button
                type='button'
                variant='ghost'
                size='icon'
                className='relative text-muted-foreground hover:text-foreground'
                aria-label={ariaLabel}
              >
                <StickyNote className='h-4 w-4' />
                {noteCount > 0 ? (
                  <span className='absolute -right-0.5 -top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-primary px-0.5 text-[10px] font-semibold leading-none text-primary-foreground'>
                    {noteCount > 9 ? '9+' : noteCount}
                  </span>
                ) : null}
              </Button>
            </PopoverTrigger>
          </TooltipTrigger>
          <TooltipContent side='bottom' className='text-xs'>
            {tooltipLabel}
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
      <PopoverContent className='w-[22rem] p-3' align='end'>
        <div className='space-y-3'>
          {isLoadingNotes ? (
            <div className='flex items-center gap-2 text-xs text-muted-foreground'>
              <Loader2 className='h-3 w-3 animate-spin' />
              Loading notes…
            </div>
          ) : sortedNotes.length > 0 ? (
            <ul
              ref={messagesListRef}
              className='max-h-36 space-y-2 overflow-y-auto rounded-md border border-border bg-muted/30 p-2'
            >
              {sortedNotes.map(note => {
                const timestamp = formatNoteTimestamp(note.created_at);
                return (
                  <li key={note.id} className='text-xs'>
                    <p className='whitespace-pre-wrap break-words'>
                      {note.body}
                    </p>
                    <p className='mt-0.5 text-muted-foreground'>
                      {formatAuthorName(note)}
                      {timestamp ? ` · ${timestamp}` : null}
                    </p>
                  </li>
                );
              })}
            </ul>
          ) : (
            <p className='text-xs text-muted-foreground'>No notes yet.</p>
          )}
          {
            <>
              <Textarea
                value={draftBody}
                onChange={event => setDraftBody(event.target.value)}
                onKeyDown={event => {
                  if (
                    event.key === 'Enter' &&
                    (event.metaKey || event.ctrlKey) &&
                    !isSaving &&
                    draftBody.trim()
                  ) {
                    event.preventDefault();
                    void handleSave();
                  }
                }}
                placeholder="Enter note here. This won't be sent along with the claim"
                textareaSize='sm'
                rows={4}
                maxLength={10_000}
                disabled={isSaving}
              />
              <div className='flex justify-end gap-2'>
                <Button
                  type='button'
                  variant='outline'
                  size='sm'
                  onClick={() => handleOpenChange(false)}
                  disabled={isSaving}
                >
                  Cancel
                </Button>
                <Button
                  type='button'
                  variant='purple'
                  size='sm'
                  onClick={() => void handleSave()}
                  disabled={isSaving || !draftBody.trim()}
                >
                  {isSaving ? (
                    <Loader2 className='h-4 w-4 animate-spin' />
                  ) : (
                    'Save'
                  )}
                </Button>
              </div>
            </>
          }
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default BillingCycleNoteButton;
